import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Bookmark, Copy } from "lucide-react";

/**
 * Wraps any content and shows a floating popover on text selection with
 * "Save Highlight" and "Copy" actions. Calls onSave({text}).
 */
export default function HighlightableText({ children, onSave, className = "", testid = "highlightable" }) {
  const ref = useRef(null);
  const [pop, setPop] = useState(null); // {x,y,text}

  useEffect(() => {
    const handleUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) { setPop(null); return; }
      const text = sel.toString().trim();
      if (!text || text.length < 3) { setPop(null); return; }
      const range = sel.getRangeAt(0);
      const container = ref.current;
      if (!container || !container.contains(range.commonAncestorContainer)) { setPop(null); return; }
      const rect = range.getBoundingClientRect();
      const cRect = container.getBoundingClientRect();
      setPop({
        x: rect.left - cRect.left + rect.width / 2,
        y: rect.top - cRect.top - 8,
        text,
      });
    };
    document.addEventListener("mouseup", handleUp);
    document.addEventListener("touchend", handleUp);
    return () => {
      document.removeEventListener("mouseup", handleUp);
      document.removeEventListener("touchend", handleUp);
    };
  }, []);

  const save = () => {
    if (pop?.text) onSave?.(pop.text);
    setPop(null);
    window.getSelection()?.removeAllRanges();
  };

  const copy = async () => {
    if (pop?.text) await navigator.clipboard.writeText(pop.text);
    setPop(null);
  };

  return (
    <div ref={ref} data-testid={testid} className={`relative ${className}`}>
      {children}
      {pop && (
        <div
          className="absolute z-50 -translate-x-1/2 -translate-y-full flex gap-1 bg-[#0A2A5E] text-white rounded-lg shadow-2xl px-1 py-1 animate-fade-up"
          style={{ left: pop.x, top: pop.y }}
        >
          <Button data-testid="popover-save-highlight" size="sm" variant="ghost" className="h-8 text-white hover:bg-[#F5A623]" onClick={save}>
            <Bookmark className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
          <Button data-testid="popover-copy" size="sm" variant="ghost" className="h-8 text-white hover:bg-[#1E5AB8]" onClick={copy}>
            <Copy className="w-3.5 h-3.5 mr-1" /> Copy
          </Button>
        </div>
      )}
    </div>
  );
}
